/* eslint-disable max-lines */
/* eslint-disable sort-keys */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Autocomplete from '@mui/material/Autocomplete';
import { useTranslate } from '@im/react-localization';
import { PresetRangePicker } from '@im/ui-lib/Calendar/PresetRangePicker';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';
import { useUserRole } from 'src/provider/UserRoleProvider';
import {
  AuditLogSearchQuery,
  useGetAuditFilters,
  useGetAuditLogVendorList,
} from '../hooks/useAuditLogsHooks';
import { useGetBemAuditEventListFilter } from 'src/views/bidManagementHub/hooks/apiHooks';
import { useProgramListQuery } from 'src/hooks/program-list';

type Props = {
  onSearch: (currentValues: Partial<AuditLogSearchQuery>) => void;
  setSearchQuery: React.Dispatch<
    React.SetStateAction<Partial<AuditLogSearchQuery>>
  >;
};

const CATEGORY_OPTIONS = ['Bid', 'BEM', 'Vendor-Program'];
const EVENT_TYPE_ALL = 'All';

const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: '560px',
    },
  },
};

const getDefaultDates = () => ({
  defaultStart: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
  defaultEnd: dayjs().format('YYYY-MM-DD'),
});

const AuditLogsFilters: React.FC<Props> = ({ onSearch, setSearchQuery }) => {
  const translate = useTranslate();
  const formatDateTime = useDateTimeFormatter();
  const userRole = useUserRole();
  const roleId = userRole?.role?.[0];
  const [searchParams, setSearchParams] = useSearchParams();
  const bidNumber = searchParams.get('bidNumber');
  const countryCode = searchParams.get('countryCode') ?? '';
  const [datePickerKey, setDatePickerKey] = useState(0);

  const { defaultEnd, defaultStart } = useMemo(() => getDefaultDates(), []);

  const [fieldValues, setFieldValues] = useState<Partial<AuditLogSearchQuery>>({
    Category: '',
    EventType: [],
    BidNumber: '',
    User: '',
    VendorId: undefined,
    VendorProgramId: undefined,
    FromDate: defaultStart,
    ToDate: defaultEnd,
  });
  const isCategorySelected = Boolean(fieldValues.Category);
  const isVendorProgramCategory = fieldValues.Category === 'Vendor-Program';

  const { data: filterData } = useGetAuditFilters(
    {
      categoryFilter: fieldValues.Category ?? '',
      RoleId: roleId ?? 0,
    },
    isCategorySelected && fieldValues.Category !== 'BEM'
  );

  const { data: bemEventList } = useGetBemAuditEventListFilter(
    {
      categoryFilter: fieldValues.Category ?? '',
      RoleId: roleId ?? 0,
    },
    fieldValues.Category === 'BEM'
  );

  const vendorList = useGetAuditLogVendorList(isCategorySelected);
  const programList = useProgramListQuery({
    countryCode,
    vendorIdToSearch:
      typeof fieldValues.VendorId === 'number' ? fieldValues.VendorId : 0,
  });

  const eventTypeOptions = useMemo(() => {
    if (fieldValues.Category === 'BEM') {
      return [EVENT_TYPE_ALL, ...(bemEventList?.auditEventList ?? [])];
    }
    return [EVENT_TYPE_ALL, ...(filterData?.auditEventList ?? [])];
  }, [fieldValues.Category, bemEventList, filterData]);

  const userOptions = useMemo(() => {
    if (fieldValues.Category === 'BEM') {
      return bemEventList?.auditUserList &&
        bemEventList.auditUserList.length > 0
        ? bemEventList.auditUserList
        : [];
    }
    return filterData?.auditUserList && filterData.auditUserList.length > 0
      ? filterData.auditUserList
      : filterData?.cogUserList ?? [];
  }, [fieldValues.Category, bemEventList, filterData]);

  const getDefaultFilterValues = useCallback(
    () => ({
      Category: '',
      EventType: [],
      BidNumber: '',
      User: '',
      VendorId: undefined,
      VendorProgramId: undefined,
      FromDate: defaultStart,
      ToDate: defaultEnd,
    }),
    [defaultStart, defaultEnd]
  );

  const handleSearch = useCallback(
    (currentValues: Partial<AuditLogSearchQuery>) => {
      const values = {
        ...currentValues,
        FromDate: currentValues.FromDate || defaultStart,
        ToDate: currentValues.ToDate || defaultEnd,
      };

      setFieldValues(values);
      setSearchQuery(values);

      onSearch(values);

      if (values.BidNumber) {
        searchParams.set('bidNumber', values.BidNumber);
      } else {
        searchParams.delete('bidNumber');
      }

      setSearchParams(searchParams, { replace: true });
    },
    [
      defaultStart,
      defaultEnd,
      onSearch,
      searchParams,
      setSearchParams,
      setSearchQuery,
    ]
  );

  const handleClearAll = useCallback(() => {
    const reset = getDefaultFilterValues();

    setFieldValues(reset);
    setSearchQuery(reset);
    setDatePickerKey((prev) => prev + 1);

    searchParams.delete('bidNumber');
    setSearchParams(searchParams, { replace: true });
  }, [getDefaultFilterValues, setSearchQuery, searchParams, setSearchParams]);

  const handleFieldChange = useCallback(
    (key: keyof AuditLogSearchQuery, value: unknown) => {
      setFieldValues((prev) => {
        const nextValues = { ...prev, [key]: value };
        if (key === 'Category' && prev.Category !== value) {
          return {
            Category: value as string,
            EventType: [],
            BidNumber: '',
            User: '',
            VendorId: undefined,
            VendorProgramId: undefined,
            FromDate: defaultStart,
            ToDate: defaultEnd,
          };
        }
        if (key === 'VendorId' && prev.VendorId !== value) {
          nextValues.VendorProgramId = undefined;
        }
        return nextValues;
      });
    },
    [defaultEnd, defaultStart]
  );

  const handleMultiSelectChange = useCallback(
    (key: keyof AuditLogSearchQuery, value: string[]) => {
      const allOptions = eventTypeOptions.filter(
        (option) => option !== EVENT_TYPE_ALL
      );

      if (value.includes(EVENT_TYPE_ALL)) {
        const currentlySelected = (fieldValues[key] as string[]) ?? [];

        if (currentlySelected.length === allOptions.length) {
          handleFieldChange(key, []);
        } else {
          handleFieldChange(key, allOptions);
        }
        return;
      }

      handleFieldChange(key, value);
    },
    [eventTypeOptions, fieldValues, handleFieldChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') handleSearch(fieldValues);
    },
    [fieldValues, handleSearch]
  );

  useEffect(() => {
    if (bidNumber) {
      setSearchQuery((prev) => ({ ...prev, BidNumber: bidNumber }));
      setFieldValues((prev) => ({ ...prev, BidNumber: bidNumber }));
    }
  }, [bidNumber, setSearchQuery]);

  const renderSelectField = (
    label: string,
    key: keyof AuditLogSearchQuery,
    options: string[],
    multiple = false,
    disabled = false,
    required = false
    // eslint-disable-next-line max-params
  ) => {
    const currentValue = fieldValues[key] ?? (multiple ? [] : '');

    return (
      <FormControl fullWidth>
        <TextField
          SelectProps={{
            MenuProps: MENU_PROPS,
            multiple,
            ...(multiple && {
              renderValue: (selected: unknown) =>
                (selected as string[]).join(', '),
            }),
          }}
          disabled={disabled}
          label={label}
          name={label}
          onChange={(event) => {
            const value = event.target.value;
            if (multiple && Array.isArray(value)) {
              handleMultiSelectChange(key, value as string[]);
            } else {
              handleFieldChange(key, value);
            }
          }}
          required={required}
          select
          value={currentValue}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {multiple && (
                <Checkbox
                  checked={
                    option === EVENT_TYPE_ALL
                      ? Array.isArray(currentValue) &&
                        currentValue.length === eventTypeOptions.length - 1
                      : Array.isArray(currentValue) &&
                        (currentValue as string[]).includes(option)
                  }
                />
              )}
              {option}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
    );
  };

  const renderUserAutocomplete = (disabled = false) => (
    <Autocomplete
      disabled={disabled}
      fullWidth
      onChange={(_, value) => handleFieldChange('User', value ?? '')}
      options={userOptions}
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{
            shrink: true,
          }}
          fullWidth
          label={translate('AuditLogs.ColumnUser')}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              height: 33,
            },
          }}
        />
      )}
      value={fieldValues.User ?? null}
    />
  );

  return (
    <Box
      id="gds-audit-logs-filters"
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      }}
    >
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, minmax(180px, 1fr))',
            gap: 2,
            alignItems: 'end',
            '.MuiInputLabel-root': {
              fontSize: '19px',
            },
          }}
        >
          {renderSelectField(
            translate('AuditLogs.ColumnCategory'),
            'Category',
            CATEGORY_OPTIONS,
            false,
            false,
            true
          )}

          {renderSelectField(
            translate('AuditLogs.ColumnEventType'),
            'EventType',
            eventTypeOptions,
            true,
            !isCategorySelected
          )}

          <FormControl fullWidth>
            <TextField
              SelectProps={{
                MenuProps: MENU_PROPS,
              }}
              disabled={!isCategorySelected}
              label={translate('AuditLogs.ColumnVendor')}
              name="Vendor"
              onChange={(event) =>
                handleFieldChange('VendorId', Number(event.target.value))
              }
              select
              value={fieldValues.VendorId ?? ''}
            >
              {vendorList.data
                ?.sort((a, b) => a.vendorName.localeCompare(b.vendorName))
                .map((vendor) => (
                  <MenuItem key={vendor.vendorID} value={vendor.vendorID}>
                    {vendor.vendorName}
                  </MenuItem>
                ))}
            </TextField>
          </FormControl>

          <FormControl fullWidth>
            <TextField
              SelectProps={{
                MenuProps: MENU_PROPS,
                multiple: true,
                renderValue: (selected) =>
                  (selected as number[])
                    .map(
                      (id) =>
                        programList.data?.vendorProgramlist.find(
                          (program) => program.vendorProgramId === id
                        )?.vendorProgramName
                    )
                    .filter(Boolean)
                    .join(', '),
              }}
              disabled={!fieldValues.VendorId}
              label={translate('AuditLogs.ColumnVendorProgram')}
              name="VendorProgram"
              onChange={(event) => {
                const value = event.target.value;
                handleFieldChange(
                  'VendorProgramId',
                  (Array.isArray(value) ? value : [value]).map(Number)
                );
              }}
              select
              value={fieldValues.VendorProgramId ?? []}
            >
              {programList.data?.vendorProgramlist
                .filter((program) => program.status === '1')
                .sort((a, b) =>
                  a.vendorProgramName.localeCompare(b.vendorProgramName)
                )
                .map((program) => (
                  <MenuItem
                    key={program.vendorProgramId}
                    value={program.vendorProgramId}
                  >
                    <Checkbox
                      checked={
                        fieldValues.VendorProgramId?.includes(
                          program.vendorProgramId
                        ) ?? false
                      }
                    />
                    {program.vendorProgramName}
                  </MenuItem>
                ))}
            </TextField>
          </FormControl>

          <FormControl fullWidth>
            <TextField
              disabled={!isCategorySelected || isVendorProgramCategory}
              fullWidth
              label={translate('AuditLogs.ColumnBidNumber')}
              onChange={(e) => handleFieldChange('BidNumber', e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={translate('AuditLogs.ColumnBidNumber')}
              value={fieldValues.BidNumber ?? ''}
            />
          </FormControl>

          {renderUserAutocomplete(!isCategorySelected)}

          <FormControl
            fullWidth
            sx={{
              '& .MuiTextField-root': { width: '100%' },
              '& .MuiOutlinedInput-root': { width: '100%' },
            }}
          >
            <PresetRangePicker
              endDate={dayjs(fieldValues.ToDate).toDate()}
              format={(sDate, eDate) =>
                !sDate || !eDate || !fieldValues.FromDate || !fieldValues.ToDate
                  ? translate('Common.DateRangePlaceholder')
                  : `${formatDateTime(sDate)} to ${formatDateTime(eDate)}`
              }
              hideClearButton
              isPreset
              key={datePickerKey}
              onDateChange={(startDate, endDate) => {
                setFieldValues({
                  ...fieldValues,
                  FromDate: dayjs(startDate).format('YYYY-MM-DD') ?? '',
                  ToDate: dayjs(endDate).format('YYYY-MM-DD') ?? '',
                });
              }}
              onIntervalSelected={(dates) => {
                setFieldValues({
                  ...fieldValues,
                  FromDate:
                    dayjs(dates.actualStartDate).format('YYYY-MM-DD') ?? '',
                  ToDate: dayjs(dates.actualEndDate).format('YYYY-MM-DD') ?? '',
                });
              }}
              startDate={dayjs(fieldValues.FromDate).toDate()}
            />
          </FormControl>
        </Box>

        {/* Actions: Search & Clear */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 1,
          }}
        >
          <Button
            disabled={!isCategorySelected}
            onClick={() => handleSearch(fieldValues)}
            startIcon={<SearchIcon />}
            variant="newContained"
          >
            {translate('Common.Search')}
          </Button>

          <Button
            onClick={handleClearAll}
            startIcon={<ClearIcon color="error" />}
            variant="newOutlined"
          >
            {translate('Common.ClearAll')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default AuditLogsFilters;
